// Helper functions for cloud account operations
import {
    CLOUD_TYPE,
    isAzure,
    isAWS,
    isGCP,
    isVSphere,
    VSPHERE_TYPES,
    isAzureLocal,
} from '../../../../constants/cloudTypes';
import { CONFIG } from '../../../../constants';

/**
 * Get external ID from session storage
 */
export const getExternalId = () => {
    const currentOrg = JSON.parse(sessionStorage.getItem('currentOrg') || '{}');
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return currentOrg.external_id || user.external_id || '';
};

/**
 * Check if downloadable cloud type (vSphere, OpenShift, etc.)
 */
export const isDownloadableCloudType = (cloudType) => {
    return [
        CLOUD_TYPE.VSPHERE,
        VSPHERE_TYPES.VSPHERE_DR,
        VSPHERE_TYPES.VSPHERE_OFFLINE_REPLICAS,
        CLOUD_TYPE.OPENSHIFT,
        CLOUD_TYPE.OPENSTACK,
        CLOUD_TYPE.NC2,
        CLOUD_TYPE.HPE_VME,
        CLOUD_TYPE.HYPERV,
    ].includes(cloudType);
};

/**
 * Build payload for cloud account creation/update
 * Edit mode only sends changed fields (mirroring backbone behavior)
 */
export const buildCloudAccountPayload = (cloudType, formData, userOrg, tags, isEditMode, additionalData = {}) => {
    const payload = {};
    const private_deployment_cloud = JSON.parse(sessionStorage.getItem('private_deployment') || "{}").cloud || "";

    // Always include type
    payload.type = cloudType;

    // For downloadable types, always include token in edit mode
    if (isEditMode && isDownloadableCloudType(cloudType) && additionalData.token) {
        payload.token = additionalData.token;
    }

    if (isEditMode) {
        // EDIT MODE: Only include fields that have changed
        const originalData = additionalData.originalData || {};

        // Check each field for changes
        if (formData.name && formData.name !== originalData.name) {
            payload.name = formData.name;
        }

        if (formData.url && formData.url !== originalData.url) {
            payload.url = formData.url;
        }

        if (formData.vsphere_version && formData.vsphere_version !== originalData.vsphere_version) {
            payload.vsphere_version = formData.vsphere_version;
        }

        if (formData.tenant_id && formData.tenant_id !== originalData.tenant_id) {
            payload.tenant_id = formData.tenant_id;
        }

        // Tags - check if changed
        const oldTags = originalData.tags || [];
        if (tags && JSON.stringify(tags.sort()) !== JSON.stringify(oldTags.sort())) {
            payload.tags = tags;
        }

        // Auth - only if credentials are being changed
        if (formData.username || formData.password) {
            if (isGCP(cloudType)) {
                // GCP in edit mode only sends role_arn (matches backbone behavior)
                if (formData.username) {
                    payload.auth = {
                        auth_type: 'role',
                        credentials: {
                            role_arn: formData.username,
                        },
                    };
                }
            } else if (isAzure(cloudType) || isAzureLocal(cloudType)) {
                // Azure requires both username and password
                if (formData.username && formData.password) {
                    payload.auth = {
                        auth_type: 'service_principal',
                        credentials: {
                            identity: formData.username,
                            secret: formData.password,
                        },
                    };
                }
            } else if (isAWS(cloudType)) {
                let authType = 'role';
                if (private_deployment_cloud == "aws") {
                    authType = 'attached_role';
                }
                // AWS uses role_arn
                if (formData.username) {
                    payload.auth = {
                        auth_type: authType,
                        credentials: {
                            role_arn: formData.username,
                            external_id: formData.password,
                        },
                    };
                }
            }
        }

        // vSphere credentials (for edit mode when checkbox is enabled)
        if (additionalData.vsphereCredentials) {
            const { username, password } = additionalData.vsphereCredentials;
            if (username && password) {
                payload.source_clouds = {
                    vsphere: {
                        auth: {
                            auth_type: 'password',
                            credentials: {
                                identity: username,
                                secret: password,
                            },
                        },
                    },
                };
            }
        }
    } else {
        // ADD MODE: Include all required fields (matching backbone createCloudPayload)
        payload.name = formData.name;
        payload.organization_id = userOrg;

        // Always include these fields with defaults (matching backbone behavior)
        payload.tenant_id = 'n/a'; // Default, will be overridden for Azure

        // URL handling
        if (isDownloadableCloudType(cloudType)) {
            payload.url = 'n/a'; // Downloadable types always use "n/a"
            payload.appliance_id = additionalData.applianceId || null;
            payload.token = additionalData.token || null;
        } else {
            payload.url = formData.url || '';
        }

        // vSphere version (only for vSphere standalone)
        if (isVSphere(cloudType)) {
            payload.vsphere_version = formData.vsphere_version || null;
        }

        // Azure-specific: override tenant_id with actual value
        if ((isAzure(cloudType) || isAzureLocal(cloudType)) && formData.tenant_id) {
            payload.tenant_id = formData.tenant_id;
        }

        // Tags
        if (tags && tags.length > 0) {
            payload.tags = tags;
        }

        // Build auth object based on cloud type
        let authType = 'password'; // Default
        let credentials = {};

        if (isAzure(cloudType) || isAzureLocal(cloudType)) {
            authType = 'service_principal';
            credentials = {
                identity: formData.username || '',
                secret: formData.password || '',
            };
        } else if (isAWS(cloudType) || isGCP(cloudType)) {
            if (private_deployment_cloud == "aws") {
                authType = 'attached_role';
            }
            else {
                authType = 'role';
            }
            const externalId = isAWS(cloudType) ? getExternalId() : formData.password;
            credentials = {
                role_arn: formData.username || '',
                external_id: externalId || '',
            };
        } else {
            // Generic password auth for other types (downloadable types)
            authType = 'password';
            credentials = {
                identity: formData.username || '',
                secret: formData.password || '',
            };
        }

        // Special case: vsphere_dr and vsphere_offline_replicas have null auth
        if (cloudType === VSPHERE_TYPES.VSPHERE_DR || cloudType === VSPHERE_TYPES.VSPHERE_OFFLINE_REPLICAS) {
            payload.auth = null;
        } else {
            payload.auth = {
                auth_type: authType,
                credentials: credentials,
            };
        }

        // DR setup specific fields (if needed in future)
        if (additionalData.drEnabled) {
            payload.dr_enabled = true;
            payload.vsphere_version = 'ESXi_6_7_U3';
        }
    }

    return payload;
};

/**
 * Build payload for connection test
 * Matches the old API format with auth object (uses createCloudPayload structure)
 */
export const buildTestConnectionPayload = (cloudType, formData, userOrg) => {
    const testPayload = {
        type: cloudType,
        name: formData.name || '',
        organization_id: userOrg,
    };

    // URL handling
    if (isDownloadableCloudType(cloudType)) {
        testPayload.url = 'n/a';
    } else {
        testPayload.url = formData.url || '';
    }

    // tenant_id - always include with default "n/a"
    testPayload.tenant_id = 'n/a';

    // Azure overrides tenant_id with actual value
    if ((isAzure(cloudType) || isAzureLocal(cloudType)) && formData.tenant_id) {
        testPayload.tenant_id = formData.tenant_id;
    }

    // vSphere version (only for vSphere standalone)
    if (isVSphere(cloudType) && formData.vsphere_version) {
        testPayload.vsphere_version = formData.vsphere_version;
    }

    // Build auth object based on cloud type
    let authType = 'password'; // Default
    let credentials = {};

    if (isAzure(cloudType) || isAzureLocal(cloudType)) {
        authType = 'service_principal';
        credentials = {
            identity: formData.username || '',
            secret: formData.password || '',
        };
    } else if (isAWS(cloudType) || isGCP(cloudType)) {
        authType = 'role';
        const private_deployment_cloud = JSON.parse(sessionStorage.getItem('private_deployment') || "{}").cloud || "";
        if (private_deployment_cloud == "aws") {
            authType = 'attached_role';
        }
        const externalId = isAWS(cloudType) ? getExternalId() : formData.password;
        credentials = {
            role_arn: formData.username || '',
            external_id: externalId || '',
        };
    } else {
        // Generic password auth for other types
        authType = 'password';
        credentials = {
            identity: formData.username || '',
            secret: formData.password || '',
        };
    }

    testPayload.auth = {
        auth_type: authType,
        credentials: credentials,
    };

    return testPayload;
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Get help documentation link
 */
export const getHelpLink = (linkKey) => {
    return CONFIG?.[linkKey] || 'http://docs.rivermeadow.com/getting-started';
};

/**
 * Get documentation link and label for downloadable cloud types
 * Returns object with { label, linkText, url }
 */
export const getDownloadableDocumentation = (cloudType) => {
    const documentationMap = {
        [CLOUD_TYPE.VSPHERE]: {
            label: 'Install appliance to source vCenter',
            linkText: 'Documentation: vSphere',
            url: getHelpLink('VSPHERE_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.OPENSHIFT]: {
            label: 'Create Image and deploy to OpenShift',
            linkText: 'Documentation: OpenShift',
            url: getHelpLink('OPENSHIFT_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.OPENSTACK]: {
            label: 'Create Image and deploy to OpenStack',
            linkText: 'Documentation: OpenStack',
            url: getHelpLink('OPENSTACK_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.NC2]: {
            label: 'Create Image and deploy to Nutanix NC2',
            linkText: 'Documentation: Nutanix NC2',
            url: getHelpLink('NC2_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.HPE_VME]: {
            label: 'Create Image and deploy to HPE VM Essentials',
            linkText: 'Documentation: HPE VM Essentials',
            url: getHelpLink('HPE_VME_APPLIANCE_DOCS'),
        },
        [CLOUD_TYPE.HYPERV]: {
            label: 'Create Image and deploy to Hyper-V',
            linkText: 'Documentation: Hyper-V',
            url: getHelpLink('HYPERV_APPLIANCE_DOCS'),
        },
    };

    return documentationMap[cloudType] || null;
};
